import { AppAction, UserRole } from "@edlab-fms/shared";
import { useEffect, useMemo, useState } from "react";
import type { ProgrammeUnitSummary } from "../lib/api/overview";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthQuery } from "../lib/api/auth";
import {
  useOverviewAttentionQuery,
  useOverviewMonthlyQuery,
  useOverviewTotalsQuery,
  useProgrammesOverviewQuery,
} from "../lib/api/overview";
import { useReportingPeriod } from "../lib/hooks/useReportingPeriod";
import { formatRoleLabel } from "../lib/format/role";
import { formatMoney } from "../lib/format/money";
import { Button } from "../components/ui/Button";
import { Card, CardHeader } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { MetricCard } from "../components/ui/MetricCard";
import { MonthSelector } from "../components/ui/MonthSelector";
import { monthLabelLong } from "../lib/overview-period";
import { PageHeader } from "../components/ui/PageHeader";
import { ProgrammeFinancialsSection } from "../features/overview/ProgrammeFinancialsSection";
import { IncomeExpenditureChart } from "../features/overview/IncomeExpenditureChart";
import { ProgrammeUnitDetailModal } from "../features/programmes/ProgrammeUnitDetailModal";
import { navItemsForRole } from "../lib/navigation";

export function OverviewPage() {
  const auth = useAuthQuery();
  const user = auth.data;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { period, setPeriod, months, fiscalYearName } = useReportingPeriod();
  const [selectedUnit, setSelectedUnit] = useState<ProgrammeUnitSummary | null>(null);

  const programmes = useProgrammesOverviewQuery(Boolean(user), period);
  const totals = useOverviewTotalsQuery(Boolean(user), period);
  const monthly = useOverviewMonthlyQuery(Boolean(user), period);
  const attention = useOverviewAttentionQuery(Boolean(user));

  const canCreateExpense = user?.permissions.actions.includes(AppAction.EXPENSE_CREATE);
  const canViewBank = totals.data?.overview.totals.bankTotal !== undefined;
  const allScope = user?.permissions.unitScope === "all";
  const canAccessTransactions = user
    ? navItemsForRole(user.role, user.unitIds).some((item) => item.to === "/transactions")
    : false;
  const isBoard = user?.role === UserRole.BOARD_MEMBER;

  useEffect(() => {
    if (searchParams.get("scroll") === "programme-financials") {
      document.getElementById("programme-financials")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [searchParams]);

  const assignedBudget = useMemo(() => {
    if (!programmes.data) {
      return "0.00";
    }
    const total = programmes.data.groups
      .flatMap((group) => group.units)
      .reduce((sum, unit) => sum + Number.parseFloat(unit.annualBudget), 0);
    return total.toFixed(2);
  }, [programmes.data]);

  const programmeFinancials = totals.data?.overview.programmes ?? [];
  const ytdPaid = totals.data?.overview.totals.ytdPaid ?? "0";
  const budgetUsedPct =
    Number.parseFloat(assignedBudget) > 0
      ? (Number.parseFloat(ytdPaid) / Number.parseFloat(assignedBudget)) * 100
      : 0;

  const pendingCount = attention.data?.attention.pendingApprovals.count ?? 0;
  const missingEvidenceCount = attention.data?.attention.missingEvidence.count ?? 0;
  const attentionCount = pendingCount + missingEvidenceCount;
  const hasAttention = attentionCount > 0;

  return (
    <div>
      <PageHeader
        eyebrow={isBoard ? "Board overview" : "Finance workspace"}
        title={
          isBoard
            ? "The foundation, at a glance."
            : user?.role === UserRole.ACCOUNTS
              ? "Your finance workspace."
              : user?.role === UserRole.PROJECT_LEAD
                ? "Hasan Academy overview."
                : "A clear view of your impact."
        }
        description={
          isBoard
            ? "A financial overview for informed decisions."
            : allScope
              ? "EDLAB Foundation · All programmes"
              : "Financials for your assigned programmes only"
        }
        actions={
          <>
            <MonthSelector value={period} onChange={setPeriod} months={months} />
            {canCreateExpense ? (
              <Button
                onClick={() => {
                  void navigate("/transactions");
                }}
              >
                New expense
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  void navigate("/reports");
                }}
              >
                View reports
              </Button>
            )}
          </>
        }
      />

      <div className="stats-grid">
        <MetricCard
          variant="hero"
          label={canViewBank ? "Bank & cash" : "Assigned programme budget"}
          value={
            canViewBank && totals.data
              ? formatMoney(totals.data.overview.totals.bankTotal ?? "0")
              : formatMoney(assignedBudget)
          }
          detail={
            canViewBank
              ? `Current balance · ${monthLabelLong(period)}`
              : fiscalYearName
                ? `Annual budget · ${fiscalYearName}`
                : "Annual budget"
          }
          loading={totals.isLoading || programmes.isLoading}
        />
        <MetricCard
          label="Income received"
          value={totals.data ? formatMoney(totals.data.overview.totals.ytdIncome) : "—"}
          detail="Financial year to selected period"
          loading={totals.isLoading}
        />
        <MetricCard
          label="Paid expenditure"
          value={totals.data ? formatMoney(ytdPaid) : "—"}
          detail={`${budgetUsedPct.toFixed(1)}% of annual budget used`}
          loading={totals.isLoading}
        />
        <MetricCard
          label="Approved & unpaid"
          value={
            totals.data ? formatMoney(totals.data.overview.totals.approvedUnpaid) : "—"
          }
          detail="Current obligations · not yet paid"
          loading={totals.isLoading}
        />
      </div>

      <div className="mt-6 grid gap-[18px] xl:grid-cols-[minmax(0,1.75fr)_minmax(280px,1fr)]">
        <Card>
          <CardHeader
            title="Income & expenditure"
            description={
              fiscalYearName
                ? `Actual receipts and payments · ${fiscalYearName}`
                : "Actual receipts and payments"
            }
          />
          {monthly.isLoading ? (
            <div className="h-[220px] animate-pulse rounded-xl bg-edlab-bg" />
          ) : monthly.data?.months.length ? (
            <IncomeExpenditureChart months={monthly.data.months} />
          ) : (
            <EmptyState
              title="No chart data"
              description="Monthly totals appear once receipts or payments are recorded."
            />
          )}
        </Card>

        <Card>
          <CardHeader
            title={user?.role === UserRole.BOARD_MEMBER ? "Items to note" : "Needs attention"}
            description="Workflow items requiring action"
            action={
              attentionCount > 0 ? (
                <span className="rounded-md bg-[#e5eeea] px-2 py-1 text-xs font-semibold text-edlab-green">
                  {attentionCount}
                </span>
              ) : undefined
            }
          />
          {attention.isLoading ? (
            <div className="grid gap-3">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="h-14 animate-pulse rounded-xl bg-edlab-bg" />
              ))}
            </div>
          ) : hasAttention ? (
            <div className="px-1 pb-1">
              {pendingCount > 0 ? (
                <div className="attention-item">
                  <span className="attention-icon" aria-hidden>
                    !
                  </span>
                  <div className="min-w-0 flex-1">
                    <b className="block text-sm font-[550] text-edlab-ink">
                      {pendingCount} expense{pendingCount === 1 ? "" : "s"} awaiting approval
                    </b>
                    <small className="mt-1 block text-xs text-[#8a939e]">
                      {formatMoney(attention.data?.attention.pendingApprovals.totalAmount ?? "0")}{" "}
                      submitted
                    </small>
                  </div>
                  {canAccessTransactions ? (
                    <Link to="/transactions?tab=submitted" className="ml-auto shrink-0">
                      <Button variant="secondary" size="small">
                        Review
                      </Button>
                    </Link>
                  ) : null}
                </div>
              ) : null}
              {missingEvidenceCount > 0 ? (
                <div className="attention-item">
                  <span className="attention-icon blue" aria-hidden>
                    ◌
                  </span>
                  <div className="min-w-0 flex-1">
                    <b className="block text-sm font-[550] text-edlab-ink">
                      {missingEvidenceCount} supporting file
                      {missingEvidenceCount === 1 ? "" : "s"} pending
                    </b>
                    <small className="mt-1 block text-xs text-[#8a939e]">
                      Keep the financial records complete before approval.
                    </small>
                  </div>
                  {canAccessTransactions ? (
                    <Link to="/transactions?tab=evidence" className="ml-auto shrink-0">
                      <Button variant="secondary" size="small">
                        View
                      </Button>
                    </Link>
                  ) : null}
                </div>
              ) : null}
              <div className="attention-item border-b-0">
                <span className="attention-icon blue" aria-hidden>
                  ↗
                </span>
                <div className="min-w-0 flex-1">
                  <b className="block text-sm font-[550] text-edlab-ink">Donor updates</b>
                  <small className="mt-1 block text-xs text-[#8a939e]">
                    Preview reports for restricted funds
                  </small>
                </div>
                <Link to="/reports?type=donor" className="ml-auto shrink-0">
                  <Button variant="secondary" size="small">
                    Open
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <EmptyState
              title="Nothing pending yet"
              description="Submitted expenses awaiting approval appear on the Transactions page under the Submitted tab."
              action={
                canAccessTransactions ? (
                  <Link to="/transactions">
                    <Button variant="secondary">View transactions</Button>
                  </Link>
                ) : undefined
              }
            />
          )}
        </Card>
      </div>

      <Card id="programme-financials" className="mt-6 scroll-mt-24">
        <CardHeader
          title="Programme financials"
          description={`${monthLabelLong(period)} and financial year to date`}
          action={
            <Link
              to="/programmes"
              className="text-sm font-medium text-edlab-green hover:underline"
            >
              View all
            </Link>
          }
        />
        {programmes.isLoading || totals.isLoading ? (
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-16 animate-pulse rounded-xl bg-edlab-bg" />
            ))}
          </div>
        ) : (
          <ProgrammeFinancialsSection
            groups={programmes.data?.groups ?? []}
            financialRows={programmeFinancials}
            period={period}
            scopeLabel={allScope ? "EDLAB Foundation" : "Assigned programmes"}
            onUnitSelect={setSelectedUnit}
          />
        )}
      </Card>

      {selectedUnit ? (
        <ProgrammeUnitDetailModal
          unit={selectedUnit}
          period={period}
          onClose={() => {
            setSelectedUnit(null);
          }}
        />
      ) : null}

      {user ? (
        <p className="mt-8 text-xs text-edlab-muted">
          Signed in as {user.name} ({formatRoleLabel(user.role)}).
          {fiscalYearName ? ` ${fiscalYearName} ·` : ""} Paid basis.
        </p>
      ) : null}
    </div>
  );
}
